import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { fetchInvoiceById, updateInvoice } from '../../store/invoiceSlice';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const EditInvoice: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentInvoice, loading, error } = useSelector((state: RootState) => state.invoices);

  const [formData, setFormData] = useState({
    amount: 0,
    taxAmount: 0,
    issueDate: '',
    dueDate: '',
    status: 'draft',
  });

  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(fetchInvoiceById(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (currentInvoice) {
      setFormData({
        amount: currentInvoice.amount || 0,
        taxAmount: currentInvoice.taxAmount || 0,
        issueDate: currentInvoice.issueDate
          ? new Date(currentInvoice.issueDate).toISOString().split('T')[0]
          : '',
        dueDate: currentInvoice.dueDate
          ? new Date(currentInvoice.dueDate).toISOString().split('T')[0]
          : '',
        status: currentInvoice.status || 'draft',
      });
    }
  }, [currentInvoice]);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: parseFloat(value) || 0 });
  };

  const calculateTotalAmount = () => {
    return formData.amount + formData.taxAmount;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(false);

    if (!id) return;

    try {
      const updateData = {
        amount: formData.amount,
        taxAmount: formData.taxAmount,
        totalAmount: calculateTotalAmount(),
        issueDate: formData.issueDate,
        dueDate: formData.dueDate,
        status: formData.status,
      };

      const result = await dispatch(updateInvoice({ id, data: updateData }));

      if (updateInvoice.fulfilled.match(result)) {
        setSuccess(true);
        setTimeout(() => {
          navigate(`/invoices/${id}`);
        }, 1500);
      }
    } catch (err) {
      console.error('Failed to update invoice:', err);
    }
  };

  if (loading && !currentInvoice) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!currentInvoice) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Invoice not found</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/invoices/${id}`)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Invoice Details
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Edit Invoice</h1>
        <p className="mt-1 text-sm text-gray-600">
          Invoice {currentInvoice.invoiceNumber}
        </p>
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-600">Invoice updated successfully! Redirecting...</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
          {/* Read-only PO Information */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <h2 className="text-sm font-semibold text-gray-900 mb-3">Immutable Information</h2>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Invoice Number:</span>
                <span className="ml-2 text-gray-900">{currentInvoice.invoiceNumber}</span>
              </div>
              <div>
                <span className="text-gray-500">Purchase Order:</span>
                <span className="ml-2 text-gray-900">{currentInvoice.poId?.poNumber}</span>
              </div>
              <div>
                <span className="text-gray-500">Buyer:</span>
                <span className="ml-2 text-gray-900">{currentInvoice.buyerId?.name}</span>
              </div>
              <div>
                <span className="text-gray-500">Supplier:</span>
                <span className="ml-2 text-gray-900">{currentInvoice.supplierId?.name}</span>
              </div>
            </div>
          </div>

          {/* Editable Invoice Details */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Invoice Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Issue Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="issueDate"
                  value={formData.issueDate}
                  onChange={handleFormChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Due Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="dueDate"
                  value={formData.dueDate}
                  onChange={handleFormChange}
                  required
                  min={formData.issueDate}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleNumberChange}
                  required
                  step="0.01"
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tax Amount
                </label>
                <input
                  type="number"
                  name="taxAmount"
                  value={formData.taxAmount}
                  onChange={handleNumberChange}
                  step="0.01"
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status <span className="text-red-500">*</span>
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleFormChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="draft">Draft</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
            </div>
          </div>

          {/* Total Amount Display */}
          <div className="pt-4 border-t border-gray-200">
            <div className="flex justify-end">
              <div className="text-right">
                <p className="text-sm text-gray-500">Total Amount</p>
                <p className="text-2xl font-bold text-gray-900">
                  ${calculateTotalAmount().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate(`/invoices/${id}`)}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditInvoice;
